import React, { useState, useEffect, useRef } from "react";
import { 
  FiFileText, 
  FiChevronDown, 
  FiX, 
  FiCheck, 
  FiInfo, 
  FiCheckCircle, 
  FiAlertCircle 
} from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import StudentReportCardView from "./tab/StudentReportCardView";
import api from "../connector/api";

// Tab Imports
import ClassroomTab from "./tab/ClassroomTab";

const REPORT_OPTIONS = [
  { id: "classroom_report", label: "Classroom", category: "Academic" },
  { id: "student_report", label: "Student Report", category: "Academic" },
  { id: "student_report_card", label: "Student Report Card", category: "Academic" },
  { id: "attendance_report", label: "Attendance Report", category: "Records" },
  { id: "employee_leave_report", label: "Employee Leave Report", category: "HR" },
  { id: "inventory_summary", label: "Inventory Summary", category: "Administrative" },
];

export default function ReportManager({ assignedClassesList = [] }) {
  // Navigation / Tabs State
  const [activeTabs, setActiveTabs] = useState([]);
  const [currentTabId, setCurrentTabId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Toast Notification State
  const [toasts, setToasts] = useState([]);

  // Layout & Global State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({ first_name: "Teacher", last_name: "User", email: "teacher@school.edu" });
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loadingPeriod, setLoadingPeriod] = useState(true);
  
  const notificationDropdownRef = useRef(null);

  // Helper to trigger toast notifications
  const showToast = (message, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);

    // Auto-remove toast after 3 seconds
    setTimeout(() => {
      removeToast(id);
    }, 3000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  useEffect(() => {
    const storedPerms = sessionStorage.getItem("permissions");
    const storedUser = sessionStorage.getItem("user");
    
    if (storedPerms) {
      try {
        setPermissions(JSON.parse(storedPerms));
      } catch (err) {
        console.error("Failed to parse permissions:", err);
      }
    }

    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        setUser({
          first_name: userObj.first_name || "Teacher",
          last_name: userObj.last_name || "User",
          username: userObj.username || "teacher",
          email: userObj.email || "teacher@school.edu"
        });
      } catch (err) {
        console.error("Failed to parse user session:", err);
      }
    }

    const fetchCurrentPeriod = async () => {
      try {
        const res = await api.get("year-terms/get-set-year-term/");
        setCurrentPeriod(res.data);
      } catch (err) {
        console.error("Failed to fetch active school year-term:", err);
        showToast("Failed to fetch current academic period", "error");
      } finally {
        setLoadingPeriod(false);
      }
    };
    fetchCurrentPeriod();
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  // Safe String Extractions
  const yearDisplay = 
    currentPeriod?.school_year_detail?.name || 
    (typeof currentPeriod?.school_year === 'object' ? currentPeriod?.school_year?.name : currentPeriod?.school_year) || 
    "2025/2026";

  const termDisplay = 
    currentPeriod?.term_detail?.name || 
    (typeof currentPeriod?.term === 'object' ? currentPeriod?.term?.name : currentPeriod?.term) || 
    "Term 1";

  // Handle selecting an item from the custom dropdown
  const handleSelectReport = (report) => {
    setIsDropdownOpen(false);

    const exists = activeTabs.some((tab) => tab.id === report.id);

    if (!exists) {
      setActiveTabs((prev) => [...prev, report]);
      showToast(`Opened "${report.label}" tab`, "success");
    } else {
      showToast(`Switched to "${report.label}"`, "info");
    }

    setCurrentTabId(report.id);
  };

  // Close an individual tab
  const handleCloseTab = (e, report) => {
    e.stopPropagation();
    const updatedTabs = activeTabs.filter((tab) => tab.id !== report.id);
    setActiveTabs(updatedTabs);

    showToast(`Closed "${report.label}"`, "info");

    if (currentTabId === report.id) {
      if (updatedTabs.length > 0) {
        setCurrentTabId(updatedTabs[updatedTabs.length - 1].id);
      } else {
        setCurrentTabId(null);
      }
    }
  };

  const activeReport = activeTabs.find((tab) => tab.id === currentTabId);

  return (
    <div className="flex h-screen w-full bg-zinc-50/50 overflow-hidden font-dynamic-title relative">
      {/* Toast Notification Floating Container */}
      <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2 max-w-sm pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg shadow-lg border text-xs font-medium transition-all duration-300 animate-in fade-in slide-in-from-bottom-2 ${
              toast.type === "success"
                ? "bg-emerald-900/90 border-emerald-700 text-emerald-100"
                : toast.type === "error"
                ? "bg-rose-900/90 border-rose-700 text-rose-100"
                : "bg-zinc-900/90 border-zinc-700 text-zinc-100"
            }`}
          >
            <div className="flex items-center gap-2">
              {toast.type === "success" && <FiCheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />}
              {toast.type === "error" && <FiAlertCircle className="w-4 h-4 text-rose-400 shrink-0" />}
              {toast.type === "info" && <FiInfo className="w-4 h-4 text-zinc-400 shrink-0" />}
              <span>{toast.message}</span>
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="p-0.5 hover:bg-white/10 rounded transition-colors text-zinc-400 hover:text-white"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Fixed Sidebar */}
      <Sidebar
        className="no-print"
        permissions={permissions}
        user={user}
        teacherSubjects={assignedClassesList}
        handleLogout={handleLogout}
        onOpenChangePassword={() => setIsModalOpen(true)}
      />

      {/* Main Layout Container */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Top Header */}
        <Header
          loading={loadingPeriod}
          loadingPeriod={loadingPeriod}
          yearDisplay={yearDisplay}
          termDisplay={termDisplay}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          notificationDropdownRef={notificationDropdownRef}
        />

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto flex flex-col min-w-0 custom-scroll">
          {/* Page Sub-Header / Selector Bar */}
          <div className="p-4 bg-white border-b border-zinc-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
            <div>
              <h1 className="text-sm font-bold text-zinc-900 tracking-tight">Report Manager</h1>
              <p className="text-[11px] text-zinc-400">Select reports to view and switch between opened tabs.</p>
            </div>

            {/* Custom Dropdown */}
            <div className="relative w-full sm:w-64">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium"
              >
                <div className="flex items-center gap-2 truncate">
                  <FiFileText className="w-4 h-4 text-zinc-400 shrink-0" />
                  <span className="truncate">Select a Report...</span>
                </div>
                <FiChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {/* Custom Dropdown Menu */}
              {isDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-20" 
                    onClick={() => setIsDropdownOpen(false)} 
                  />
                  <div className="absolute right-0 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-30 max-h-60 overflow-y-auto custom-scroll">
                    <div className="px-3 py-1.5 border-b border-zinc-100 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                      Available Reports
                    </div>
                    {REPORT_OPTIONS.map((report) => {
                      const isOpen = activeTabs.some((tab) => tab.id === report.id);
                      return (
                        <button
                          key={report.id}
                          onClick={() => handleSelectReport(report)}
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition-colors"
                        >
                          <div className="min-w-0 pr-2">
                            <p className="text-xs font-semibold text-zinc-800 truncate">{report.label}</p>
                            <p className="text-[10px] text-zinc-400">{report.category}</p>
                          </div>
                          {isOpen && <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Dynamic Tabs Bar */}
          {activeTabs.length > 0 && (
            <div className="flex items-center gap-1 px-4 pt-2 bg-white border-b border-zinc-200/80 overflow-x-auto scrollbar-none shrink-0">
              {activeTabs.map((tab) => {
                const isActive = tab.id === currentTabId;
                return (
                  <div
                    key={tab.id}
                    onClick={() => setCurrentTabId(tab.id)}
                    className={`group flex items-center gap-2 px-3 py-2 rounded-t-lg border-t border-x cursor-pointer transition-all text-xs font-semibold shrink-0 select-none ${
                      isActive
                        ? "bg-zinc-50 border-zinc-200/80 text-zinc-900 border-b-transparent -mb-px"
                        : "bg-zinc-100/60 border-transparent text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
                    }`}
                  >
                    <FiFileText className={`w-3.5 h-3.5 ${isActive ? "text-zinc-700" : "text-zinc-400"}`} />
                    <span>{tab.label}</span>
                    <button
                      onClick={(e) => handleCloseTab(e, tab)}
                      className="p-0.5 rounded-md hover:bg-zinc-200/80 text-zinc-400 hover:text-zinc-700 transition-colors"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Report Workspace Panel */}
          <div className="flex-1 p-4">
            {activeReport ? (
              <div className="min-h-[400px]">
                {/* Conditional View Rendering */}
                {activeReport.id === "student_report_card" ? (
                  <StudentReportCardView />
                ) : activeReport.id === "classroom_report" ? (
                  <ClassroomTab />
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-zinc-200 rounded-lg text-zinc-400 space-y-2">
                    <FiFileText className="w-10 h-10 text-zinc-300" />
                    <p className="text-xs font-semibold text-zinc-600">
                      {activeReport.label} View Container
                    </p>
                    <p className="text-[11px] text-zinc-400">
                      Place your report table, analytical charts, or exports for {activeReport.label} here.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 border border-dashed border-zinc-200 rounded-xl bg-white/50 text-center">
                <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-400 mb-3">
                  <FiFileText className="w-5 h-5" />
                </div>
                <p className="text-xs font-bold text-zinc-800">No Report Selected</p>
                <p className="text-[11px] text-zinc-400 max-w-xs mt-0.5">
                  Select a report from the custom menu dropdown above to open it in a viewable tab.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}